import React, {useEffect, useState} from "react";
import {Button} from "primereact/button";

import {getKeyValue} from "../../utils/Utils";

import ScrapeStatus from "./ScrapeStatus";
import {Panel} from "primereact/panel";
import {RadioButton} from "primereact/radiobutton";
import {InputNumber} from 'primereact/inputnumber';
import {CampaignService} from "../../service/CampaignService";
import {Column} from "primereact/column";
import {DataTable} from "primereact/datatable";
import {ScraperService} from "../../service/ScraperService";
import {AppContext} from "../../HomePage";
import {Loading} from "../../utils/InlineComponents";
import {QueueService} from "../../service/QueueService";

// Every button on this tab falls into exactly one of three tiers, and the tier
// is what decides how the button looks. The screen used to pick a colour per
// button on vibes ("failed reviews" was red only because of the word "failed"),
// which told an admin nothing about what pressing it would cost.
//
//   SAFE  - local recompute, no scraping, repeating it changes nothing.
//   JOB   - queues a live scraping job: costs scraping units and finishes
//           later, but repeating it only wastes units.
//   WRITE - changes campaign data the moment it is pressed. Filled, not
//           outlined, so it is the only solid button on the screen.
const TIER_SAFE = "p-button-outlined p-button-secondary";
const TIER_JOB = "p-button-outlined p-button-info";
const TIER_WRITE = "p-button-danger";

const hintStyle = {color: 'var(--text-color-secondary)', fontSize: '0.85em', marginTop: '.35em', lineHeight: '1.35'};

const ProductAdminAction = (props) => {

    const {t, toast, setError} = React.useContext(AppContext);
    const [loading, setLoading] = useState(false);


    let selectedProduct = props.selectedProduct;
    const campaignService = new CampaignService();
    const scraperService = new ScraperService();
    const queueService = React.useMemo(() => new QueueService(), []);
    const [campaignCompetitors, setCampaignCompetitors] = useState(null)

    const [filters, setFilters] = useState({
        match_method: "",
        min_match_score: 70,
    });

    useEffect(() => {
        setLoading(true);
        selectedProduct = props.selectedProduct;
        campaignService.getCampaignCompetitors(selectedProduct.campaign_id, false).then((competitors) => {
            let campaignCompetitors = competitors.filter(comp => comp.is_site_searching_ready);
            setCampaignCompetitors(campaignCompetitors);
            setLoading(false);
        }).catch(error =>{
            setError(error);
            setError(null);
            setLoading(false);
        });
    }, [props.selectedProduct]);

    const newSearchRequest = {
        campaign_id: selectedProduct.campaign_id,
        product_id: selectedProduct.id,
        type: 'competitor_suggestion_product',
        spider_name: "competitor_suggestion_product"
    }

    const onChange = (e) => {
        const {key, value} = getKeyValue(e);
        setFilters({...filters, [key]: value});
    };

    const actionTemplate = (rowData) => {
        return <React.Fragment>
            <Button icon="pi pi-search-plus" className={TIER_JOB} tooltip={t('product_admin.hint_site_search_row')}
                    style={{marginRight: '.5em'}} disabled={!rowData.is_site_searching_ready} onClick={() => searchCompetitorsOnWebsite(rowData)}/>
        </React.Fragment>;
    };

    const searchCompetitorsOnWebsite = (rowData) => {
        scraperService.saveScrapingRequest({
            type: "site_search_product",
            campaign_id: selectedProduct.campaign_id,
            product_id: selectedProduct.id,
            url: rowData.url
        }).then(() => {
            toast.current.show({severity: 'success', summary: t('actions.search_competitors_on_website'), detail: t('message_detail.successful')});
        });
    };

    const recomputeScores = () => {
        queueService.createQueueItem('compute_match_scores', { product_id: selectedProduct.id })
            .then(() => toast.current.show({
                severity: 'success',
                summary: t('suggestion.recompute_scores'),
                detail: t('message_detail.request_queued'),
            }))
            .catch(err => setError(err));
    };

    // One action: the button carries the tier styling, the line underneath says
    // what pressing it actually starts.
    const actionCell = (label, hint, icon, className, onClick) =>
        <div className="p-col-12 p-md-6 p-lg-4">
            <Button icon={icon} className={className} label={label} onClick={onClick} style={{width: '100%'}}/>
            <div style={hintStyle}>{hint}</div>
        </div>;

    // The legend items are real buttons with pointer events off, so each swatch
    // is literally the style it is explaining.
    const legendItem = (className, text) =>
        <Button className={`${className} p-button-sm`} label={text} tabIndex={-1}
                style={{marginRight: '.5em', marginBottom: '.25em', pointerEvents: 'none'}}/>;

    return <div className="p-grid p-col-12">

        <div className="p-col-12 p-pb-0">
            <div style={hintStyle}>{t('product_admin.legend_intro')}</div>
            <div className="p-mt-2">
                {legendItem(TIER_SAFE, t('product_admin.legend_safe'))}
                {legendItem(TIER_JOB, t('product_admin.legend_job'))}
                {legendItem(TIER_WRITE, t('product_admin.legend_write'))}
            </div>
        </div>

        <div className="p-col-12">
            <Panel header={t('product_admin.group_suggestions')}>
                <div className="p-grid">

                    <div className="p-col-12">
                        <b>{t('table_headers.find_matches_status')}</b>
                        <div style={hintStyle}>{t('product_admin.hint_suggestion_status')}</div>
                        <div className="p-mt-2">
                            <ScrapeStatus type="suggest" scraping_request={selectedProduct.competitor_suggestion_scraping_request}
                                          campaign_id={selectedProduct.campaign_id}
                                          product_id={selectedProduct.id}
                                          competitor_url={null}
                                          newRequest={newSearchRequest}
                                          newRequestCompleted={props.refreshProducts}/>
                        </div>
                    </div>

                    {actionCell(t('actions.search_competitors_on_website'), t('product_admin.hint_site_search_all'),
                        "pi pi-search-plus", TIER_JOB, props.searchCompetitorsOnWebsite)}

                    {actionCell(t('suggestion.recompute_scores'), t('product_admin.hint_recompute_scores'),
                        "pi pi-refresh", TIER_SAFE, recomputeScores)}

                    <div className="p-col-12">
                        <Panel header={t('suggestion.match_method')}>

                            <RadioButton inputId="match_method_sku" value="sku" name="match_method" onChange={onChange} checked={filters.match_method === "sku"}/>
                            <label htmlFor="match_method_sku" className="p-ml-1 p-mr-1">{t('product.sku')}</label>

                            <RadioButton inputId="match_method_mpn" value="mpn" name="match_method" onChange={onChange} checked={filters.match_method === "mpn"}/>
                            <label htmlFor="match_method_mpn" className="p-ml-1 p-mr-1">{t('product.mpn')}</label>

                            <RadioButton inputId="match_method_ean" value="ean" name="match_method" onChange={onChange} checked={filters.match_method === "ean"}/>
                            <label htmlFor="match_method_ean" className="p-ml-1 p-mr-1">{t('product.ean')}</label>

                            <RadioButton inputId="match_method_upc" value="upc" name="match_method" onChange={onChange} checked={filters.match_method === "upc"}/>
                            <label htmlFor="match_method_upc" className="p-ml-1 p-mr-1">{t('product.upc')}</label>

                            <RadioButton inputId="match_method_barcode" value="barcode" name="match_method" onChange={onChange} checked={filters.match_method === "barcode"}/>
                            <label htmlFor="match_method_barcode" className="p-ml-1 p-mr-1">{t('product.barcode')}</label>

                            <RadioButton inputId="match_method_name_exact" value="name_exact" name="match_method" onChange={onChange} checked={filters.match_method === "name_exact"}/>
                            <label htmlFor="match_method_name_exact" className="p-ml-1 p-mr-1">{t('suggestion.name_exact')}</label>

                            <RadioButton inputId="match_method_name_in" value="name_in" name="match_method" onChange={onChange} checked={filters.match_method === "name_in"}/>
                            <label htmlFor="match_method_name_in" className="p-ml-1 p-mr-1">{t('suggestion.name_in')}</label>

                            <RadioButton inputId="match_method_match_score" value="match_score" name="match_method" onChange={onChange} checked={filters.match_method === "match_score"}/>
                            <label htmlFor="match_method_match_score" className="p-ml-1 p-mr-1">{t('suggestion.match_score')}</label>
                            {filters.match_method === "match_score" && <InputNumber name="min_match_score" value={filters.min_match_score} style={{
                                width: "7%",
                                marginLeft: '.5em'
                            }} onValueChange={onChange} showButtons min={0} max={100} size={2}/>}

                            <Button icon="pi pi-link" className={TIER_WRITE}
                                    label={t('actions.auto_match_suggestions')} style={{width: "25%", marginLeft: '.5em'}}
                                    onClick={() => props.autoMatchProductSuggestions(filters.match_method, filters.min_match_score)}/>

                            <div style={hintStyle}>{t('product_admin.hint_auto_match')}</div>

                        </Panel>
                    </div>

                    <div className="p-col-12">
                        <b>{t('product_admin.site_search_per_site')}</b>
                        <div style={hintStyle}>{t('product_admin.hint_site_search_row')}</div>
                    </div>

                    {loading ?
                        <div className="p-col-12" style={{textAlign: 'center'}}>
                            <Loading/>
                        </div> :
                        campaignCompetitors && <div className="p-col-12">
                            <DataTable value={campaignCompetitors} dataKey="url" responsive={true}>
                                <Column field="url" header={t('campaign.competitors.url')} sortable/>
                                <Column body={actionTemplate} header={t('table_headers.action')} style={{textAlign: 'center', width: '10em'}}/>
                            </DataTable>
                        </div>
                    }
                </div>
            </Panel>
        </div>

        <div className="p-col-12">
            <Panel header={t('product_admin.group_prices')}>
                <div className="p-grid">
                    {actionCell(t('actions.get_shipping_price'), t('product_admin.hint_shipping_price'),
                        "pi pi-shopping-cart", TIER_JOB, props.getShippingPrices)}
                </div>
            </Panel>
        </div>

        <div className="p-col-12">
            <Panel header={t('product_admin.group_reviews')}>
                <div className="p-grid">
                    {actionCell(t('actions.get_product_reviews'), t('product_admin.hint_product_reviews'),
                        "pi pi-comments", TIER_JOB, props.getProductReviews)}

                    {actionCell(t('actions.get_product_failed_reviews'), t('product_admin.hint_failed_reviews'),
                        "pi pi-replay", TIER_JOB, props.getProductFailedReviews)}
                </div>
            </Panel>
        </div>

    </div>;

};
export default ProductAdminAction;
